import { useQuery } from '@apollo/client';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Label } from '@artstorefronts/ui';
import type { StrategySettings } from '@arthelper/shared/acquisition/strategy';
import AdminLayout from '../../../components/AdminLayout';
import { ACQUISITION_STRATEGIES, type Strategy } from './queries';
import { METRIC_ROWS, StatusBadge, formatDate, label } from './shared';

const SELECT_CLASS = 'h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-base md:text-sm';
const TH = 'px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500';
const TD = 'px-4 py-3 text-sm align-top';

interface SettingRow {
  path: string;
  left: string;
  right: string;
}

/** `{ signupGate: { mode } }` → `[['signupGate.mode', 'open']]`, for a row-per-setting diff. */
function flatten(value: unknown, prefix = ''): Array<[string, string]> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return [[prefix, JSON.stringify(value)]];
  }
  return Object.entries(value as Record<string, unknown>).reduce<Array<[string, string]>>((memo, [key, child]) => {
    return [...memo, ...flatten(child, prefix ? `${prefix}.${key}` : key)];
  }, []);
}

function diffSettings(left: StrategySettings, right: StrategySettings): SettingRow[] {
  const leftMap = new Map(flatten(left));
  const rightMap = new Map(flatten(right));
  const paths = Array.from(new Set([...leftMap.keys(), ...rightMap.keys()]));
  return paths.map((path) => {
    return { path, left: leftMap.get(path) ?? '—', right: rightMap.get(path) ?? '—' };
  });
}

function StrategyPicker({
  id,
  value,
  strategies,
  onChange,
  title,
}: {
  id: string;
  value: string;
  strategies: Strategy[];
  onChange: (id: string) => void;
  title: string;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{title}</Label>
      <select id={id} className={SELECT_CLASS} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Choose a version…</option>
        {strategies.map((s) => (
          <option key={s.id} value={s.id}>
            {label(s)} ({s.status})
          </option>
        ))}
      </select>
    </div>
  );
}

function Header({ strategy }: { strategy: Strategy }) {
  return (
    <div className="space-y-1">
      <Link to={`/admin/acquisition/${strategy.id}`} className="font-medium text-gray-900 hover:underline">
        {label(strategy)}
      </Link>
      <div className="flex flex-wrap items-center gap-2 text-xs font-normal normal-case tracking-normal text-gray-500">
        <StatusBadge status={strategy.status} />
        <span>
          {formatDate(strategy.activatedAt)} – {formatDate(strategy.deactivatedAt)}
        </span>
      </div>
    </div>
  );
}

function SideBySide({ left, right }: { left: Strategy; right: Strategy }) {
  const settingRows = diffSettings(left.settings, right.settings);
  const changed = settingRows.filter((row) => row.left !== row.right).length;

  return (
    <div className="space-y-6">
      <section className="overflow-x-auto rounded-lg bg-white shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={TH}>Metric</th>
              <th className={TH}>
                <Header strategy={left} />
              </th>
              <th className={TH}>
                <Header strategy={right} />
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {METRIC_ROWS.map((row) => (
              <tr key={row.label}>
                <td className={`${TD} text-gray-700`}>
                  {row.label}
                  {row.hint && <div className="text-xs text-gray-400">{row.hint}</div>}
                </td>
                <td className={`${TD} tabular-nums text-gray-900`}>{row.value(left.metrics)}</td>
                <td className={`${TD} tabular-nums text-gray-900`}>{row.value(right.metrics)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="overflow-x-auto rounded-lg bg-white shadow">
        <div className="px-4 pt-4">
          <h2 className="text-lg font-semibold">Settings</h2>
          <p className="text-xs text-gray-500">
            {changed === 0 ? 'Identical settings.' : `${changed} setting${changed === 1 ? '' : 's'} differ.`}
          </p>
        </div>
        <table className="mt-2 min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={TH}>Setting</th>
              <th className={TH}>v{left.version}</th>
              <th className={TH}>v{right.version}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {settingRows.map((row) => {
              const differs = row.left !== row.right;
              return (
                <tr key={row.path} className={differs ? 'bg-yellow-50' : undefined}>
                  <td className={`${TD} whitespace-nowrap font-mono text-xs text-gray-600`}>{row.path}</td>
                  <td className={`${TD} min-w-48 break-words font-mono text-xs text-gray-900`}>{row.left}</td>
                  <td className={`${TD} min-w-48 break-words font-mono text-xs text-gray-900`}>{row.right}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>
    </div>
  );
}

function AcquisitionComparePage() {
  const [params, setParams] = useSearchParams();
  const leftId = params.get('left') ?? '';
  const rightId = params.get('right') ?? '';
  const { data, loading } = useQuery<{ acquisitionStrategies: Strategy[] }>(ACQUISITION_STRATEGIES, {
    fetchPolicy: 'cache-and-network',
  });
  const strategies = data?.acquisitionStrategies ?? [];
  const left = strategies.find((s) => s.id === leftId);
  const right = strategies.find((s) => s.id === rightId);

  const choose = (side: 'left' | 'right', id: string) => {
    const next = new URLSearchParams(params);
    if (id === '') {
      next.delete(side);
    } else {
      next.set(side, id);
    }
    setParams(next, { replace: true });
  };

  return (
    <AdminLayout title="Compare strategies" description="Any two versions, metrics and settings side by side.">
      <div className="space-y-6 px-4 md:px-0">
        <Link
          to="/admin/acquisition"
          className="inline-flex min-h-11 items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="h-4 w-4" />
          All strategies
        </Link>

        <div className="grid gap-4 rounded-lg bg-white p-4 shadow sm:grid-cols-2">
          <StrategyPicker
            id="compare-left"
            title="First version"
            value={leftId}
            strategies={strategies}
            onChange={(id) => choose('left', id)}
          />
          <StrategyPicker
            id="compare-right"
            title="Second version"
            value={rightId}
            strategies={strategies}
            onChange={(id) => choose('right', id)}
          />
        </div>

        {loading && strategies.length === 0 && <div className="text-gray-500">Loading strategies…</div>}
        {left && right && <SideBySide left={left} right={right} />}
        {!(left && right) && strategies.length > 0 && (
          <p className="text-sm text-gray-500">Pick two versions to compare.</p>
        )}
      </div>
    </AdminLayout>
  );
}

export default AcquisitionComparePage;
